/**
 * Companies API
 */
import apiClient from './index'
import type {
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  Company,
  CompanyCreateData,
  CompanyUpdateData,
  CompanyFilters,
  Contact,
  Project,
  Deal,
} from '@/types'

export const companiesApi = {
  /**
   * List companies with filtering and pagination
   */
  async list(params?: PaginationParams & CompanyFilters): Promise<PaginatedResponse<Company>> {
    const response = await apiClient.get<PaginatedResponse<Company>>('/companies', { params })
    return response.data
  },

  /**
   * Get company by ID
   */
  async get(id: string): Promise<Company> {
    const response = await apiClient.get<ApiResponse<Company>>(`/companies/${id}`)
    return response.data.data
  },

  /**
   * Create a new company
   */
  async create(data: CompanyCreateData): Promise<Company> {
    const response = await apiClient.post<ApiResponse<Company>>('/companies', data)
    return response.data.data
  },

  /**
   * Update a company
   */
  async update(id: string, data: CompanyUpdateData): Promise<Company> {
    const response = await apiClient.put<ApiResponse<Company>>(`/companies/${id}`, data)
    return response.data.data
  },

  /**
   * Delete a company
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/companies/${id}`)
  },

  /**
   * Get company contacts
   */
  async getContacts(id: string, params?: PaginationParams): Promise<PaginatedResponse<Contact>> {
    const response = await apiClient.get<PaginatedResponse<Contact>>(`/companies/${id}/contacts`, { params })
    return response.data
  },

  /**
   * Get company projects
   */
  async getProjects(id: string, params?: PaginationParams): Promise<PaginatedResponse<Project>> {
    const response = await apiClient.get<PaginatedResponse<Project>>(`/companies/${id}/projects`, { params })
    return response.data
  },

  /**
   * Get company deals
   */
  async getDeals(id: string, params?: PaginationParams): Promise<PaginatedResponse<Deal>> {
    const response = await apiClient.get<PaginatedResponse<Deal>>(`/companies/${id}/deals`, { params })
    return response.data
  },
}
